// Revolt Color Picker Component
// Popup-based color picker with predefined colors and custom HSV color picker

import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback
} from 'react';
import ReactDOM from 'react-dom';
import { RevoltButton } from './index';
import './RevoltColorPicker.css';

// HSV/HSB Color conversion utilities
const rgbToHsv = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  const s = max === 0 ? 0 : diff / max;
  const v = max;

  if (diff !== 0) {
    switch (max) {
      case r: h = (g - b) / diff + (g < b ? 6 : 0); break;
      case g: h = (b - r) / diff + 2; break;
      case b: h = (r - g) / diff + 4; break;
      default: h = 0; break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, v * 100];
};

const hsvToRgb = (h, s, v) => {
  h /= 360;
  s /= 100;
  v /= 100;

  const c = v * s;
  const x = c * (1 - Math.abs((h * 6) % 2 - 1));
  const m = v - c;

  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 1/6) {
    r = c; g = x; b = 0;
  } else if (1/6 <= h && h < 2/6) {
    r = x; g = c; b = 0;
  } else if (2/6 <= h && h < 3/6) {
    r = 0; g = c; b = x;
  } else if (3/6 <= h && h < 4/6) {
    r = 0; g = x; b = c;
  } else if (4/6 <= h && h < 5/6) {
    r = x; g = 0; b = c;
  } else if (5/6 <= h && h < 1) {
    r = c; g = 0; b = x;
  }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ];
};

const hsvToHex = (h, s, v) => {
  const [r, g, b] = hsvToRgb(h, s, v);
  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToHsv = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return rgbToHsv(r, g, b);
};

// Utility function for clamping values
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

// Basic color palette - can be overridden via props (4x4 grid)
const DEFAULT_PREDEFINED_COLORS = [
  // Row 1
  '#ff0000', // Red
  '#ff8000', // Orange
  '#ffff00', // Yellow
  '#80ff00', // Lime
  // Row 2
  '#00ff00', // Green
  '#00ff80', // Spring green
  '#00ffff', // Cyan
  '#0080ff', // Sky blue
  // Row 3
  '#0000ff', // Blue
  '#8000ff', // Purple
  '#ff00ff', // Magenta
  '#ff0080', // Pink
  // Row 4
  '#ffffff', // White
  '#808080', // Gray
  '#000000', // Black
  'transparent', // No color
];


export const RevoltColorPicker = React.forwardRef(function RevoltColorPicker({
  value,
  onChange,
  disabled = false,
  className = '',
  showPredefined = true,
  predefinedColors = DEFAULT_PREDEFINED_COLORS,
  ...props
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  // Parse initial value and alpha
  const parseInitialValue = (value) => {
    if (!value) return { hsv: { h: 0, s: 100, v: 100 }, alpha: 100 };

    // Check if value includes alpha (#RRGGBBAA format)
    let hexValue = value;
    let alphaValue = 100;

    if (value.length === 9 && value.startsWith('#')) {
      // Extract alpha from the last 2 characters
      const alphaHex = value.slice(7);
      alphaValue = Math.round((parseInt(alphaHex, 16) / 255) * 100);
      hexValue = value.slice(0, 7); // Remove alpha part
    }

    const [h, s, v] = hexToHsv(hexValue);
    return { hsv: { h, s, v }, alpha: alphaValue };
  };

  const initialState = parseInitialValue(value);
  const [hsv, setHsv] = useState(initialState.hsv);
  const [alpha, setAlpha] = useState(initialState.alpha);
  const [pendingNull, setPendingNull] = useState(!value);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    direction: 'down',
  });

  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);

  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  // Convert current HSV to hex
  const currentColor = hsvToHex(hsv.h, hsv.s, hsv.v);

  // Format hex value with alpha if needed
  const displayHexValue = alpha < 100
    ? `${currentColor}${Math.round((alpha / 100) * 255).toString(16).padStart(2, '0').toUpperCase()}`
    : currentColor;

  // Draw the saturation/value (brightness) canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));

    // Set actual canvas size for crisp rendering
    canvas.width = width;
    canvas.height = height;

    // Reset transforms so we draw in raw pixel space
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;

    // Create the HSV saturation/value plane for current hue
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const saturation = (x / (width - 1 || 1)) * 100;
        const value = ((height - 1 - y) / (height - 1 || 1)) * 100;

        const color = hsvToHex(hsv.h, saturation, value);
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);

        const index = (y * width + x) * 4;
        data[index] = r;
        data[index + 1] = g;
        data[index + 2] = b;
        data[index + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);

    // Draw selector circle (scale for DPI)
    const selectorX = (hsv.s / 100) * (width - 1);
    const selectorY = ((100 - hsv.v) / 100) * (height - 1);
    const outerRadius = 6 * dpr;
    const innerRadius = 4 * dpr;

    ctx.beginPath();
    ctx.arc(selectorX, selectorY, outerRadius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(selectorX, selectorY, innerRadius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1 * dpr;
    ctx.stroke();
  }, [hsv]);

  // Handle canvas click/drag
  const handleCanvasInteraction = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));

    // Convert coordinates directly to HSV values
    // X: Saturation (0% left, 100% right)
    // Y: Value/Brightness (100% top, 0% bottom)
    const s = (x / rect.width) * 100;
    const v = ((rect.height - y) / rect.height) * 100; // Invert Y

    setHsv(prev => ({ ...prev, s, v }));
  }, []);

  const handleCanvasMouseDown = useCallback((event) => {
    isDraggingRef.current = true;
    handleCanvasInteraction(event);
  }, [handleCanvasInteraction]);

  const handleCanvasMouseMove = useCallback((event) => {
    if (isDraggingRef.current) {
      handleCanvasInteraction(event);
    }
  }, [handleCanvasInteraction]);

  const handleCanvasMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Update HSV, alpha, and pendingNull when value prop changes
  useEffect(() => {
    const parsed = parseInitialValue(value);
    setHsv(parsed.hsv);
    setAlpha(parsed.alpha);
    setPendingNull(!value);
  }, [value]);

  // Redraw canvas when HSV changes
  useEffect(() => {
    if (isOpen) {
      drawCanvas();
    }
  }, [isOpen, drawCanvas]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousemove', handleCanvasMouseMove);
      document.addEventListener('mouseup', handleCanvasMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleCanvasMouseMove);
        document.removeEventListener('mouseup', handleCanvasMouseUp);
      };
    }
  }, [isOpen, handleCanvasMouseMove, handleCanvasMouseUp]);

  const updatePopupPosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const popupEl = popupRef.current;
    if (!triggerEl || !popupEl) {
      return;
    }

    const triggerRect = triggerEl.getBoundingClientRect();
    const popupRect = popupEl.getBoundingClientRect();
    const viewportPadding = 20;

    // Determine preferred direction
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const shouldOpenUp = spaceBelow < popupRect.height + viewportPadding &&
                        spaceAbove > spaceBelow;

    // Calculate preferred position
    let left = triggerRect.left;
    let top = shouldOpenUp
      ? triggerRect.top - popupRect.height - viewportPadding
      : triggerRect.bottom + viewportPadding;

    // Clamp to viewport bounds (exactly like RevoltTooltip)
    left = clamp(left, viewportPadding, window.innerWidth - popupRect.width - viewportPadding);
    top = clamp(top, viewportPadding, window.innerHeight - popupRect.height - viewportPadding);

    const finalPosition = {
      top,
      left,
      direction: shouldOpenUp ? 'up' : 'down',
    };

    setPopupPosition(finalPosition);
  }, []);

  const openPopup = useCallback(() => {
    if (disabled || isOpen) {
      return;
    }
    updatePopupPosition();
    setIsOpen(true);
  }, [disabled, isOpen, updatePopupPosition]);

  const handleSwatchClick = () => {
    if (!disabled) {
      if (isOpen) {
        handleCancel();
      } else {
        openPopup();
      }
    }
  };

  // Committed display for trigger swatch (value prop only - not live preview)
  const committedDisplayColor = value
    ? (value.length === 9 ? value.slice(0, 7) : value)
    : '#cccccc';
  const committedOpacity = value
    ? (value.length === 9 ? parseInt(value.slice(7, 9), 16) / 255 : 1)
    : 0.5;

  const handleColorSelect = (color) => {
    setPendingNull(false);
    const [h, s, v] = hexToHsv(color);
    setHsv({ h, s, v });
  };

  const handleSelectTransparent = () => {
    setPendingNull(true);
    setAlpha(0);
  };

  const handleConfirm = () => {
    if (onChange) {
      if (pendingNull) {
        onChange(null);
      } else {
        onChange(displayHexValue);
      }
    }
    setPendingNull(!value);
    setIsOpen(false);
  };

  const handleCancel = useCallback(() => {
    setPendingNull(!value);
    if (value) {
      const parsed = parseInitialValue(value);
      setHsv(parsed.hsv);
      setAlpha(parsed.alpha);
    } else {
      setHsv({ h: 0, s: 100, v: 100 });
      setAlpha(100);
    }
    setIsOpen(false);
  }, [value]);

  // Handle outside clicks - discard changes (same as Cancel)
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      ) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, handleCancel]);

  // Position popup after it becomes visible and DOM is updated (like RevoltTooltip)
  useLayoutEffect(() => {
    if (isOpen) {
      updatePopupPosition();
    }
  }, [isOpen, updatePopupPosition]);

  // Handle window resize/scroll to reposition popup
  useLayoutEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => {
      updatePopupPosition();
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updatePopupPosition]);

  const pickerClasses = [
    'revolt-color-picker',
    className
  ].filter(Boolean).join(' ');

  const popup = (
      <div
        ref={(el) => {
          popupRef.current = el;
        }}
        className={`color-picker-popup color-picker-popup--${popupPosition.direction}`}
        style={{
          top: popupPosition.top,
          left: popupPosition.left,
        }}
      >
      <div className="color-picker-content">
        <div className="color-picker-section">
          <h3 className="color-picker-section-title">Color Picker</h3>
          <div className="hsl-color-picker">
            <div className="color-canvas-container">
              <canvas
                ref={canvasRef}
                className="color-canvas"
                onMouseDown={handleCanvasMouseDown}
              />
            </div>
            <div className="color-sliders">
              <div className="slider-row">
                <label className="slider-label">Hue</label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={hsv.h}
                  onChange={(e) => setHsv(prev => ({ ...prev, h: parseInt(e.target.value) }))}
                  className="color-slider hue-slider"
                />
                <span className="slider-value">{Math.round(hsv.h)}°</span>
              </div>
              <div className="slider-row">
                <label className="slider-label">Alpha</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={alpha}
                  onChange={(e) => setAlpha(parseInt(e.target.value))}
                  className="color-slider alpha-slider"
                />
                <span className="slider-value">{alpha}%</span>
              </div>
            </div>

            {showPredefined && (
              <div className="color-grid">
              {predefinedColors.map((color) => {
                const isTransparent = color === 'transparent';
                let isSelected = false;

                if (isTransparent) {
                  isSelected = !value;
                } else {
                  // Compare HSV values for selection
                  const [colorH, colorS, colorV] = hexToHsv(color);
                  isSelected = Math.abs(hsv.h - colorH) < 5 &&
                              Math.abs(hsv.s - colorS) < 5 &&
                              Math.abs(hsv.v - colorV) < 5;
                }

                return (
                  <button
                    key={color}
                    type="button"
                    className={`color-option ${isSelected ? 'selected' : ''} ${isTransparent ? 'transparent' : ''}`}
                    style={isTransparent ? {} : { backgroundColor: color }}
                    onClick={() => isTransparent ? handleSelectTransparent() : handleColorSelect(color)}
                    title={isTransparent ? 'No color' : color}
                  />
                );
              })}
              </div>
            )}

            <div className="color-preview">
              <div
                className="color-swatch large"
                style={{
                  backgroundColor: currentColor,
                  opacity: alpha / 100
                }}
              />
              <span className="color-hex-value">{displayHexValue}</span>
            </div>
          </div>
        </div>

        <div className="color-picker-actions">
          <RevoltButton variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </RevoltButton>
          <RevoltButton variant="primary" size="sm" onClick={handleConfirm}>
            Confirm
          </RevoltButton>
        </div>
      </div>
    </div>
  );

  return (
    <div className={pickerClasses} ref={ref}>
      <div className="color-picker-display">
        <div
          className="color-swatch"
          style={{
            backgroundColor: committedDisplayColor,
            opacity: committedOpacity
          }}
          onClick={handleSwatchClick}
          title={value ? `Current color: ${displayHexValue}` : 'Choose color'}
          ref={triggerRef}
        />
      </div>

      {isOpen ? ReactDOM.createPortal(popup, document.body) : null}
    </div>
  );
});